import React, { useState, useMemo } from "react";
import { LineChart, XAxis, YAxis, CartesianGrid, Line, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from "recharts";
import { AnalyticsTooltip } from './common/AnalyticsTooltip';

const PopupModal = ({ title, children, onClose }) => (
    <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
    >
        <div 
            className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col"
            onClick={e => e.stopPropagation()}
        >
            <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-bold font-nunito text-gray-800">{title}</h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold font-nunito">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
                {children}
            </div>
        </div>
    </div>
);

const TripItinerary = ({ itinerary }) => {
    if (!itinerary || !itinerary.stops || itinerary.stops.length === 0) {
        return <p className="text-gray-500">No trip itinerary available for this route.</p>;
    }

    const formatAddress = (fullAddress) => {
        if (!fullAddress) return "N/A";
        const parts = fullAddress.split(',');
        return parts.length >= 2 ? `${parts[0].trim()}, ${parts[1].trim()}` : fullAddress;
    };
    
    return (
        <div>
            <h4 className="text-lg font-semibold text-gray-700 mb-2">Trip Itinerary</h4>
            <div className="max-h-80 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                        <tr>
                            <th scope="col" className="px-4 py-3 font-nunito">Stop</th>
                            <th scope="col" className="px-4 py-3 font-nunito">Address</th>
                            <th scope="col" className="px-4 py-3 font-nunito">SoC (Arr.)</th>
                            <th scope="col" className="px-4 py-3 font-nunito">SoC (Dep.)</th>
                            <th scope="col" className="px-4 py-3 font-nunito">Charged (kWh)</th>
                            <th scope="col" className="px-4 py-3 font-nunito">Cost</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y">
                        {itinerary.stops.map((stop) => (
                            <tr key={stop.stop_number} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-medium font-nunito text-gray-900">{stop.type}</td>
                                <td className="px-4 py-3 font-nunito">{formatAddress(stop.address)}</td>
                                <td className="px-4 py-3 font-nunito">{stop.charging?.soc_before?.toFixed(1) || '-'}%</td>
                                <td className="px-4 py-3 font-nunito">{stop.charging?.soc_after?.toFixed(1) || '-'}%</td>
                                <td className={`px-4 py-3 font-nunito ${stop.charging?.is_charged ? 'text-green-600 font-semibold' : ''}`}>{stop.charging?.amount_charged_kwh?.toFixed(1) || '-'}</td>
                                <td className={`px-4 py-3 font-nunito ${stop.charging?.is_charged ? 'text-green-600 font-semibold' : ''}`}>${stop.charging?.charging_cost?.toFixed(2) || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export const AnalyticsRouteSelection = ({ route, onSelect, isSelected }) => {
    const [showPopup, setShowPopup] = useState(false);

    const handleClick = () => onSelect(isSelected ? null : route.route_id);

    const chartData = useMemo(() => {
        if (!route?.charging_schedule || !route?.battery_levels) return [];
        return Object.keys(route.charging_schedule).map(timePeriod => ({
            timePeriod,
            timeOfDay: `${Math.floor(parseInt(timePeriod) * 15 / 60).toString().padStart(2, '0')}:${((parseInt(timePeriod) * 15) % 60).toString().padStart(2, '0')}`,
            chargingPower: route.charging_schedule[timePeriod].charging_power || 0,
            batteryLevel: route.battery_levels[timePeriod].energy_level || 0,
            socPercentage: route.battery_levels[timePeriod].soc_percentage || 0,
            stopRatio: route.stop_ratios?.[timePeriod] || 0
        }));
    }, [route]);

    return (
        <>
            <button
                type="button"
                aria-pressed={isSelected}
                onClick={handleClick}
                className={`w-full p-4 border rounded-lg text-left transition-all duration-300 flex flex-col relative
                    ${isSelected ? "bg-blue-50 border-blue-500 shadow-md" : "bg-white hover:bg-gray-50 border-gray-200"}
                    ${route.is_electric ? "border-l-4 border-l-green-500" : "border-l-4 border-l-red-500"}
                `}
            >
                <span className="font-bold font-nunito text-gray-800 text-lg mb-2"><span className="font-nunito">Route:</span> {route.route_name}</span>
                <div className="flex flex-col gap-1 text-sm text-gray-600">
                    <span className="flex items-center">
                        <span className="font-nunito">Status:</span>
                        <span className={`font-semibold ml-2 flex items-center ${route.is_electric ? 'text-green-600' : 'text-red-600'}`}>
                            <span className={`inline-block mr-2 rounded-full h-3 w-3 ${route.is_electric ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            <span className="font-nunito">{route.is_electric ? "Electric" : "Diesel"}</span>
                        </span>
                    </span>
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); setShowPopup(true); }}
                    className="mt-3 py-1 px-3 bg-blue-500 text-white text-xs font-semibold font-nunito rounded-md hover:bg-blue-600 self-start"
                >
                    Show Details
                </button>
            </button>

            {showPopup && (
                <PopupModal title={`Route ${route.route_name} Details`} onClose={() => setShowPopup(false)}>
                    <div className="text-base text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <p><strong className="font-nunito">Status:</strong> <span className={route.is_electric ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{route.is_electric ? "Electric" : "Diesel"}</span></p>
                        <p><strong className="font-nunito">Truck Model:</strong> {route.is_electric ? (route.selected_model || "Not specified") : "Diesel Truck"}</p>
                    </div>
                    
                    <TripItinerary itinerary={route.trip_itinerary} />

                    {chartData.length > 0 && (
                        <div className="space-y-6 mt-6">
                            <div className="p-4 border rounded-lg bg-gray-50">
                                <h4 className="text-lg font-semibold text-gray-700 mb-2">Charging Schedule (kW)</h4>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} />
                                        <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="charging" />} />
                                        <Line type="monotone" dataKey="chargingPower" name="Charging Power" stroke="#8884d8" strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="p-4 border rounded-lg bg-gray-50">
                                <h4 className="text-lg font-semibold text-gray-700 mb-2">State of Charge (%)</h4>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="timeOfDay" tick={{ fontSize: 10 }} />
                                        <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 10 }} />
                                        <Tooltip content={(props) => <AnalyticsTooltip {...props} chartType="soc" />} />
                                        <Line type="monotone" dataKey="socPercentage" name="State of Charge" stroke="#82ca9d" strokeWidth={2} dot={false} />
                                         {chartData.map((entry, index) => (
                                            entry.stopRatio > 0 && index < chartData.length - 1 && (
                                                <ReferenceArea key={index} x1={entry.timeOfDay} x2={chartData[index + 1].timeOfDay} fill="#8884d8" fillOpacity={0.2} label={{ value: "Stop", position: "insideTop", fill: "#666", fontSize: 10 }} />
                                            )
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </PopupModal>
            )}
        </>
    );
};